import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

const PropertyDocumentsSection = ({ propertyId, leases }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [uploadDocumentType, setUploadDocumentType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Documents' },
    { value: 'Insurance', label: 'Insurance' },
    { value: 'Inspection', label: 'Inspection Reports' },
    { value: 'Registration', label: 'Property Registration' },
    { value: 'Contract', label: 'Contracts' },
    { value: 'Photo', label: 'Photos' },
    { value: 'Safety', label: 'Safety Certificates' },
    { value: 'Permit', label: 'Permits' },
    { value: 'Tax', label: 'Tax Documents' },
    { value: 'Other', label: 'Other' }
  ];

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fetchDocuments = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !propertyId) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('description', uploadDescription);
      formData.append('category', uploadCategory);
      formData.append('document_type', uploadDocumentType);

      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        await fetchDocuments();
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadDescription('');
        setUploadCategory('Other');
        setUploadDocumentType('');
      } else {
        alert('Error uploading document');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchDocuments();
      } else {
        alert('Error deleting document');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting document');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return '📎';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    return '📎';
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? documents 
    : documents.filter(doc => doc.category === selectedCategory);

  return (
    <div className="property-documents-section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Property Documents</h2>
        <button 
          onClick={() => setShowUploadModal(true)}
          className="add-attribute-button"
          style={{ padding: '10px 20px' }}
        >
          📤 Upload Document
        </button>
      </div>

      {/* Category Filter */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {categories.map(cat => (
          <button
            key={cat.value}
            onClick={() => setSelectedCategory(cat.value)}
            style={{
              padding: '6px 12px',
              border: selectedCategory === cat.value ? '2px solid #1457f5' : '1px solid #e2e8f0',
              background: selectedCategory === cat.value ? '#e7f0ff' : 'white',
              color: selectedCategory === cat.value ? '#1457f5' : '#64748b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Lease Documents Reference Section */}
      {leases && leases.length > 0 && (
        <div style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Lease Documents</h3>
          <p style={{ color: '#64748b', marginBottom: '15px' }}>
            Access lease documents from tenant document rooms:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
            {leases.map(lease => (
              <div key={lease.id} style={{ 
                padding: '15px', 
                background: 'white', 
                borderRadius: '8px', 
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>{lease.tenant_name}</div>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                  Unit {lease.unit_number}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                  Lease: {lease.start_date} - {lease.end_date || 'Ongoing'}
                </div>
                <a 
                  href={`/tenant/${lease.tenant_id}#documents`}
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    background: '#1457f5',
                    color: 'white',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '14px'
                  }}
                >
                  View Documents →
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading documents...</div>
      ) : filteredDocuments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          {selectedCategory === 'all' ? 'No documents uploaded yet.' : `No ${categories.find(c => c.value === selectedCategory)?.label.toLowerCase()} documents.`}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {filteredDocuments.map(doc => (
            <div key={doc.id} style={{
              padding: '20px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '32px', marginRight: '12px' }}>
                  {getFileIcon(doc.file_type)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px', wordBreak: 'break-word' }}>
                    {doc.original_filename || doc.filename || 'Unknown file'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {doc.category && <span style={{ marginRight: '8px' }}>{doc.category}</span>}
                    {formatDate(doc.upload_date)}
                  </div>
                </div>
              </div>
              {doc.description && (
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px', flex: 1 }}>
                  {doc.description}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {formatFileSize(doc.file_size)}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a
                    href={`${API_BASE_URL}/api/documents/${doc.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '6px 12px',
                      background: '#1457f5',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '12px'
                    }}
                  >
                    Download
                  </a>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    style={{
                      padding: '6px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Upload Document</h2>
            <form onSubmit={handleUpload}>
              <div className="form-field">
                <label>File *</label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  required
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>
              <div className="form-field">
                <label>Category *</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="form-input"
                  required
                >
                  {categories.filter(c => c.value !== 'all').map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Document Type</label>
                <input
                  type="text"
                  value={uploadDocumentType}
                  onChange={(e) => setUploadDocumentType(e.target.value)}
                  className="form-input"
                  placeholder="e.g., Annual Insurance Policy, Lease Agreement"
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Optional description"
                />
              </div>
              <div className="modal-actions">
                <button 
                  type="submit"
                  className="save-button"
                  disabled={uploading || !uploadFile}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="cancel-button"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDocumentsSection;

